import React, { useState, useEffect } from "react";

import CloudUploadModal from "../Modals/CloudUploadModal";
import ReferenceModal from "../Modals/ReferenceModal";
import LocalUploadModal from "../Modals/LocalUploadModal";
import MeasurementsAPI from "../../API/measurements.api";
import { Line } from "rc-progress";
import { addKeyIfExists } from "../../utils/Helpers";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import SwitchButton from "../Buttons/SwitchButton";
import ScansAPI from "../../API/scans.api";
import { Trans, useTranslation } from "react-i18next";
import StepsNavButtons from "../Buttons/StepsNavButtons";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import { isEmpty } from "lodash";

const MEASUREMENTS_PER_REQUEST = 250;

const AddCalibration = ({
    data,
    setData,
    dataFileName,
    setDataFileName,
    dataReference,
    setDataReference,
    dataReferenceFileName,
    setDataReferenceFileName,
    settings,
    setSettings,
    nextStep,
    prevStep,
    selectedMaterial,
    selectedMaterialName,
    setModifiedDataReference,
    nextStepButtonText,
    isTesting,
    setMaxAllowedKFold,
    setHasMissingRef,
    hasMissingRef,
}) => {
    const [cloudModal, setCloudModal] = useState(false);
    const [measurementsProgressBarValue, setMeasurementsProgressBarValue] =
        useState(0);
    const [referenceProgressBarValue, setReferenceProgressBarValue] =
        useState(0);
    const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
    const [isLocalUploadModalOpen, setIsLocalUploadModalOpen] = useState(false);
    const [isLocal, setIsLocal] = useState(true);

    const [measurementAppliedFilters, setMeasurementAppliedFilters] = useState(
        {}
    );

    const [scanAppliedFilters, setScanAppliedFilters] = useState({});

    const { t } = useTranslation();

    const SCANS_FILTER_OPTIONS = [
        {
            label: t("materialName"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: t("capturedAt"),
            name: "capturedAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
    ];

    const MEASUREMENTS_FILTER_OPTIONS = [
        {
            label: t("materialName"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
    ];

    useEffect(() => {
        if (data) {
            setMeasurementsProgressBarValue(100);
            setIsLocal(dataFileName !== "NeoSpectra Cloud");
        }

        if (dataReference) {
            setReferenceProgressBarValue(100);
        }
    }, []);

    const openCloudModal = () => {
        setCloudModal(true);
    };

    const resetReferences = () => {
        setDataReference({});
        setDataReferenceFileName(null);
    };

    const getCloudMeasurementData = async (_data, _filters) => {
        let startIndex = 0,
            totalCount,
            totalData = [];
        if (!_data.length) return totalData;
        do {
            const res = await MeasurementsAPI.getMeasurementsDetails(
                MEASUREMENTS_PER_REQUEST,
                _data,
                startIndex,
                _filters
            );
            startIndex += 1;
            totalCount = res?.totalCount ? res.totalCount : 0;
            !isEmpty(res) && totalData.push(res);
            setMeasurementsProgressBarValue(
                Math.ceil(totalData.length / totalCount) * 100
            );

            if (totalCount <= startIndex * MEASUREMENTS_PER_REQUEST) break;
        } while (true);
        return totalData;
    };

    const getCloudScansData = async (_data, _filters) => {
        let startIndex = 0,
            totalCount,
            totalData = [];
        if (!_data.length) return totalData;
        do {
            const res = await ScansAPI.getScanMeasurementsWithDetails(
                MEASUREMENTS_PER_REQUEST,
                _data,
                startIndex,
                _filters
            );
            startIndex += 1;
            totalCount = res?.totalCount ? res.totalCount : 0;
            totalData.push(res);
            setMeasurementsProgressBarValue(
                Math.ceil(totalData.length / totalCount) * 100
            );

            if (totalCount <= startIndex * MEASUREMENTS_PER_REQUEST) break;
        } while (true);
        return totalData;
    };

    const applyCloudData = async (
        measurementData,
        scanData,
        { _measurementFilters, _scanFilters }
    ) => {
        try {
            resetReferences();
            setDataFileName("NeoSpectra Cloud");

            const _measurements = await getCloudMeasurementData(
                measurementData,
                _measurementFilters
            );
            const _scans = await getCloudScansData(scanData, _scanFilters);

            const totalData = [..._measurements, ..._scans];
            const headers = totalData[0].headers;

            let rows = [],
                ids = [],
                distinct = [],
                newSettings = [],
                metadata = {};

            const DEFINED_METADATA = ["scanner", "createdAt", "createdBy"];

            for (let row of totalData) {
                const measurements = row.measurements || row.scans;
                newSettings = row.settings;
                for (const measurement of measurements) {
                    const dataRow = [
                        measurement.name,
                        ...measurement.spectralData,
                    ];
                    rows.push(dataRow);
                    ids.push({ id: measurement.id, key: measurement.key });
                    for (const key of DEFINED_METADATA) {
                        addKeyIfExists(measurement, key, metadata);
                    }

                    if (!isTesting && !distinct.includes(measurement.name)) {
                        distinct.push(measurement.name);
                    }
                }
            }
            if (!isTesting) {
                setMaxAllowedKFold(distinct.length);
            }
            setData({ headers, rows, metadata, ids });
            setSettings(newSettings);
            SuccessAlert(t("dataRetrievedSuccessfully"));
            return {
                headers,
                rows,
                metadata,
                ids,
            };
        } catch (error) {
            ErrorAlert(
                t("dataRetrieveFail", {
                    error,
                })
            );
        }
    };

    const canSkip = () => {
        return isTesting && !data;
    };

    const isNextStepDisabled = () => {
        if (canSkip()) return false;

        return (
            !data ||
            !dataReference.rows?.length ||
            measurementsProgressBarValue !== 100 ||
            referenceProgressBarValue !== 100 ||
            hasMissingRef
        );
    };

    const handleRemoveData = (isCalibrationData) => {
        if (isCalibrationData) {
            setData({});
            setDataFileName(null);
            setMeasurementsProgressBarValue(0);
        }
        setDataReference({});
        setDataReferenceFileName(null);
        setModifiedDataReference({});
        setReferenceProgressBarValue(0);
    };

    return (
        <>
            <div className="steps step-2 show" id="step-two">
                <div className="title-with-desc-section mb-6">
                    <div className="flex flex-wrap items-center justify-between mb-2">
                        <h3 className="mb-0">
                            <Trans i18nKey="step2Header">
                                Upload Measurements & Reference
                            </Trans>
                            {isTesting && (
                                <span className="text-xs text-primary-orange">
                                    <Trans i18nKey="optionalWithBrackets">
                                        (optional)
                                    </Trans>
                                </span>
                            )}
                        </h3>

                        <SwitchButton
                            firstButton={t("local")}
                            secondButton={t("cloud")}
                            isActive={isLocal}
                            setIsActive={setIsLocal}
                            firstIcon="upload"
                            secondIcon="cloud-upload-alt"
                        />
                    </div>
                    <p>
                        <Trans i18nKey="uploadNeospectraMeasurements">
                            Upload NeoSpectra measurements that were taken using
                            NeoCollect or NeoScan SW/App as calibration data
                            set. Please refer to NeoCollect user guide for more
                            details.
                        </Trans>
                    </p>
                </div>

                {dataFileName && (
                    <div className="title-with-desc-section">
                        <h3>
                            <Trans i18nKey="uploadedFiles">
                                Uploaded Files
                            </Trans>
                        </h3>

                        {/* REFERENCE PROGRESS BAR */}
                        {dataFileName && (
                            <div className="relative pt-1">
                                <div className="flex mb-2 items-center justify-between">
                                    <div>
                                        <span className="text-xs font-semibold inline-block py-1 px-2">
                                            {measurementsProgressBarValue !==
                                            100 ? (
                                                <span className="uppercase text-primary-blue">
                                                    <Trans i18nKey="uploadingMeasurementsInProgress">
                                                        Uploading Measurements
                                                        in progress...
                                                    </Trans>
                                                </span>
                                            ) : (
                                                <>
                                                    <span className="uppercase text-primary-blue">
                                                        <Trans i18nKey="measurementsUploaded">
                                                            Measurements
                                                            Uploaded:
                                                        </Trans>
                                                    </span>

                                                    <span className="mx-2 font-medium blue-color">
                                                        {dataFileName}
                                                    </span>
                                                </>
                                            )}
                                            <i
                                                onClick={() =>
                                                    handleRemoveData(true)
                                                }
                                                className="fas fa-times-circle text-primary-orange fa-md cursor-pointer ml-2"
                                            />
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-xs font-semibold inline-block text-primary-blue">
                                            {measurementsProgressBarValue}%
                                        </span>
                                    </div>
                                </div>
                                <Line
                                    percent={measurementsProgressBarValue}
                                    strokeWidth="1"
                                    strokeColor="#4ac990"
                                />
                            </div>
                        )}

                        {/* REFERENCE PROGRESS BAR */}
                        {dataReferenceFileName && (
                            <div className="relative pt-1">
                                <div className="flex mb-2 items-center justify-between">
                                    <div>
                                        <span className="text-xs font-semibold inline-block py-1 px-2">
                                            {referenceProgressBarValue !==
                                            100 ? (
                                                <span className="uppercase text-primary-blue">
                                                    <Trans i18nKey="uploadingReferenceInProgress">
                                                        Uploading Reference in
                                                        progress...
                                                    </Trans>
                                                </span>
                                            ) : (
                                                <>
                                                    <span className="uppercase text-primary-blue">
                                                        <Trans i18nKey="referenceUploaded">
                                                            Reference Uploaded:
                                                        </Trans>
                                                    </span>
                                                    <span className="mx-2 font-medium blue-color">
                                                        {dataReferenceFileName}
                                                    </span>
                                                </>
                                            )}
                                            <i
                                                onClick={() =>
                                                    handleRemoveData()
                                                }
                                                className="fas fa-times-circle text-primary-orange fa-md cursor-pointer ml-2"
                                            />
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-xs font-semibold inline-block text-primary-blue">
                                            {referenceProgressBarValue}%
                                        </span>
                                    </div>
                                </div>
                                <Line
                                    percent={referenceProgressBarValue}
                                    strokeWidth="1"
                                    strokeColor="#4ac990"
                                />
                            </div>
                        )}
                    </div>
                )}
                <div className="upload-area">
                    <div className="upload-items card-table shadow relative">
                        <img
                            src={
                                require("../../assets/images/upload-local-small.png")
                                    .default
                            }
                            alt="Upload Measurments"
                        />
                        <h4>
                            <Trans i18nKey="localMeasurements">
                                Local Measurments
                            </Trans>
                        </h4>
                        <p>
                            <Trans i18nKey="uploadMeasurementsParagraph">
                                Upload NeoSpectra measurements that were taken
                                and exported using NeoSpectra Collect SW/App as
                                calibration dataset.
                            </Trans>
                        </p>
                        <button
                            onClick={() => setIsLocalUploadModalOpen(true)}
                            className="secondary-green-btn bottom-btn"
                            disabled={!isLocal}
                            id="import-measurements-btn"
                        >
                            <Trans i18nKey="importMeasurements">
                                Import Measurements
                            </Trans>
                        </button>
                    </div>
                    <div className="upload-items card-table shadow relative">
                        <img
                            src={
                                require("../../assets/images/upload-cloud-small.png")
                                    .default
                            }
                            alt="Upload"
                        />
                        <h4>
                            <Trans i18nKey="cloudMeasurements">
                                Cloud Measurments
                            </Trans>
                        </h4>
                        <p>
                            <Trans i18nKey="retrieveSyncedMeasurementsParagraph">
                                Retrieve synced NeoSpectra measurements that
                                were taken using NeoSpectra Collect SW/App as
                                calibration dataset.
                            </Trans>
                        </p>
                        <button
                            onClick={openCloudModal}
                            className="secondary-green-btn bottom-btn"
                            disabled={isLocal}
                            id="upload-cloud-measurements-btn"
                        >
                            <Trans i18nKey="uploadMeasurements">
                                Upload Measurements
                            </Trans>
                        </button>
                    </div>
                    <div className="upload-items card-table shadow relative">
                        <img
                            src={
                                require("../../assets/images/upload-material-small.png")
                                    .default
                            }
                            alt="Upload"
                        />
                        <h4>
                            <Trans i18nKey="importReference">
                                Import Reference
                            </Trans>
                        </h4>
                        <p>
                            <Trans i18nKey="uploadReferenceParagraph">
                                Upload reference values to the same samples that
                                were measured by NeoSpectra sensor.
                            </Trans>
                        </p>
                        <button
                            onClick={() =>
                                setIsReferenceModalOpen(!isReferenceModalOpen)
                            }
                            disabled={measurementsProgressBarValue !== 100}
                            className="secondary-green-btn bottom-btn"
                            id="upload-references-btn"
                        >
                            <Trans i18nKey="uploadReferences">
                                Upload References
                            </Trans>
                        </button>
                    </div>
                </div>
            </div>
            <StepsNavButtons
                back={{ action: prevStep }}
                next={{
                    action: (e) => (canSkip() ? nextStep(e, 6) : nextStep(e)),
                    disabled: isNextStepDisabled(),
                    label: canSkip() ? t("skip") : nextStepButtonText,
                }}
            />
            <CloudUploadModal
                isVisible={cloudModal}
                setIsVisible={setCloudModal}
                applyCloudData={applyCloudData}
                scansFilterOptions={SCANS_FILTER_OPTIONS}
                measurmentsFilterOptions={MEASUREMENTS_FILTER_OPTIONS}
                scanAppliedFilters={scanAppliedFilters}
                measurementAppliedFilters={measurementAppliedFilters}
                setScanAppliedFilters={setScanAppliedFilters}
                setMeasurementAppliedFilters={setMeasurementAppliedFilters}
                selectedMaterialName={selectedMaterialName}
            />
            <ReferenceModal
                isVisible={isReferenceModalOpen}
                setIsVisible={setIsReferenceModalOpen}
                dataReference={dataReference}
                data={data}
                setReferenceData={setDataReference}
                setDataReferenceFileName={setDataReferenceFileName}
                setReferenceProgressBarValue={setReferenceProgressBarValue}
                setModifiedDataReference={setModifiedDataReference}
                setHasMissingRef={setHasMissingRef}
            />

            <LocalUploadModal
                modalButton={isLocalUploadModalOpen}
                modalButtonSetter={setIsLocalUploadModalOpen}
                data={data}
                setData={setData}
                settings={settings}
                setSettings={setSettings}
                selectedMaterial={selectedMaterial}
                setMeasurementsProgressBarValue={
                    setMeasurementsProgressBarValue
                }
                resetReferences={resetReferences}
                setMaxAllowedKFold={setMaxAllowedKFold}
                setDataFileName={setDataFileName}
                isTesting={isTesting}
            />
        </>
    );
};

export default AddCalibration;
